import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Leaf, BookOpen, PenLine, TrendingUp, Calendar, GraduationCap, Target, Crown, Menu, Globe, Users, Brain, MessageCircle, Music, ChevronDown, Shield, Dumbbell, Heart, User, LogOut, Settings as SettingsIcon, Sparkles, Puzzle, Gem, FolderKanban, Contact, Wallet, Shuffle, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import ThemeSwitcher from '@/components/ThemeSwitcher';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useFeatureFlags } from '@/contexts/FeaturesContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { performLogout } from '@/utils/tokenUtils';

export const Navigation = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { clearSubscription } = useSubscription();
  const coldCallFlags = useFeatureFlags('cold_call_trainer');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const email = localStorage.getItem('userEmail');
    let role = '';
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      role = u.role || '';
    } catch { /* noop */ }
    setIsLoggedIn(!!token);
    setUserEmail(email || '');
    setUserRole(role);

    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await performLogout();
    clearSubscription();
    navigate('/login');
  };
  
  // Grouped navigation structure for better organization
  const navGroups = {
    main: [
      { path: '/', label: t('nav.home'), icon: Leaf },
    ],
    tools: [
      { path: '/practices', label: t('nav.practices'), icon: Dumbbell },
      { path: '/focus', label: t('nav.focus'), icon: Brain },
      { path: '/goal-counter', label: i18n.language === 'de' ? 'Ziel Counter' : 'Goal Counter', icon: Target },
      { path: '/focus/games', label: i18n.language === 'de' ? 'Konzentrationsspiele' : 'Focus Games', icon: Puzzle },
      { path: '/journal', label: t('nav.journal'), icon: PenLine },
      { path: '/projects', label: t('nav.projects'), icon: FolderKanban },
      { path: '/leisure-planner', label: i18n.language === 'de' ? 'Freizeitplaner' : 'Leisure Planner', icon: Shuffle },
      { path: '/kontakt-basis', label: 'Kontakt Basis', icon: Contact },
      { path: '/budget', label: i18n.language === 'de' ? 'Budget Planer' : 'Budget Planner', icon: Wallet },
      ...(coldCallFlags.enabled && (!coldCallFlags.superadminOnly || userRole === 'superadmin')
        ? [{ path: '/cold-call-trainer', label: i18n.language === 'de' ? 'Kaltakquise-Trainer' : 'Cold Call Trainer', icon: Phone }]
        : []),
      { path: '/progress', label: t('nav.progress'), icon: TrendingUp, isSeparator: true },
    ],
    growth: [
      { path: '/chatbot', label: i18n.language === 'de' ? 'Alltags-Coach' : 'Daily Life Coach', icon: Shield },
      { path: '/learning', label: t('nav.learning'), icon: GraduationCap },
      { path: '/luxury', label: i18n.language === 'de' ? 'Dein Luxus' : 'Your Luxury', icon: Gem },
    ],
    community: [
      { path: '/environment', label: t('nav.environment'), icon: Users },
      { path: '/relationship-readiness', label: i18n.language === 'de' ? 'Beziehungsfähigkeit' : 'Relationship Readiness', icon: Heart },
    ],
    single: [
      { path: '/calendar', label: t('nav.calendar'), icon: Calendar },
      { path: '/music', label: t('nav.music'), icon: Music },
    ]
  };

  // Flat list for mobile
  const navItems = [
    ...navGroups.main,
    ...navGroups.tools,
    ...navGroups.growth,
    ...navGroups.community,
    ...navGroups.single,
  ];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('preferredLanguage', lng);
  };

  const getUserInitials = () => {
    if (!userEmail) return 'U';
    const parts = userEmail.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return userEmail.substring(0, 2).toUpperCase();
  };

  return (
    <motion.nav 
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled 
          ? 'backdrop-blur-xl bg-white/80 dark:bg-slate-950/80 shadow-lg border-b border-white/20' 
          : 'bg-gradient-to-r from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 border-b border-slate-200/50 dark:border-slate-800/50'
      }`}
    >
      {/* Premium Gradient Overlay for Logged In Users */}
      {isLoggedIn && (
        <div className="absolute inset-0 bg-gradient-to-r from-[#006039]/5 via-[#00915a]/5 to-[#004d2e]/5 pointer-events-none" />
      )}

      <div className="container mx-auto px-4 py-3 relative">
        <div className="flex items-center justify-between gap-4">
          {/* Logo - Custom Image */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative"
            >
              <img 
                src="/logo.png" 
                alt="AsceticLife Logo" 
                className="h-10 w-10 sm:h-12 sm:w-12 object-contain rounded-lg"
              />
            </motion.div>
            <div className="hidden sm:block">
              <span className="text-xl font-serif font-bold text-[#006039] dark:text-emerald-400">
                AsceticLife
              </span>
              {isLoggedIn && (
                <span className="block text-[10px] font-medium text-[#006039]/70 dark:text-emerald-400/70 tracking-wider uppercase">
                  Premium Edition
                </span>
              )}
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2 flex-1 justify-end">
            {/* Home */}
            <Link to="/">
              <Button
                variant={location.pathname === '/' ? 'default' : 'ghost'}
                size="sm"
                className={`gap-2 transition-all ${
                  location.pathname === '/' 
                    ? 'bg-[#006039] hover:bg-[#005432] text-white shadow-md' 
                    : 'hover:bg-[#006039]/10 hover:text-[#006039] dark:hover:bg-[#006039]/20 dark:hover:text-emerald-400'
                }`}
              >
                <Leaf className="h-4 w-4" />
                <span>{t('nav.home')}</span>
              </Button>
            </Link>

            {/* Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-1 hover:bg-[#006039]/10 hover:text-[#006039] dark:hover:bg-[#006039]/20 dark:hover:text-emerald-400"
                >
                  <Dumbbell className="h-4 w-4" />
                  <span>{t('nav.tools') || 'Tools'}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-56 backdrop-blur-xl bg-white/95 dark:bg-slate-950/95 border-slate-200/50 dark:border-slate-800/50 shadow-xl"
              >
                {navGroups.tools.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <React.Fragment key={item.path}>
                      {item.isSeparator && <DropdownMenuSeparator />}
                      <DropdownMenuItem asChild>
                        <Link to={item.path} className="flex items-center gap-3 cursor-pointer hover:bg-[#006039]/10 dark:hover:bg-[#006039]/20">
                          <Icon className="h-4 w-4 text-[#006039] dark:text-emerald-400" />
                          <span>{item.label}</span>
                        </Link>
                      </DropdownMenuItem>
                    </React.Fragment>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Growth Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-1 hover:bg-[#006039]/10 hover:text-[#006039] dark:hover:bg-[#006039]/20 dark:hover:text-emerald-400"
                >
                  <Sparkles className="h-4 w-4" />
                  <span>{t('nav.growth') || 'Growth'}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-56 backdrop-blur-xl bg-white/95 dark:bg-slate-950/95 border-slate-200/50 dark:border-slate-800/50 shadow-xl"
              >
                {navGroups.growth.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link to={item.path} className="flex items-center gap-3 cursor-pointer hover:bg-[#006039]/10 dark:hover:bg-[#006039]/20">
                        <Icon className="h-4 w-4 text-[#006039] dark:text-emerald-400" />
                        <span>{item.label}</span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Community Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-1 hover:bg-[#006039]/10 hover:text-[#006039] dark:hover:bg-[#006039]/20 dark:hover:text-emerald-400"
                >
                  <Users className="h-4 w-4" />
                  <span>{t('nav.community') || 'Community'}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-56 backdrop-blur-xl bg-white/95 dark:bg-slate-950/95 border-slate-200/50 dark:border-slate-800/50 shadow-xl"
              >
                {navGroups.community.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link to={item.path} className="flex items-center gap-3 cursor-pointer hover:bg-[#006039]/10 dark:hover:bg-[#006039]/20">
                        <Icon className="h-4 w-4 text-[#006039] dark:text-emerald-400" />
                        <span>{item.label}</span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {navGroups.single.map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={location.pathname === item.path ? 'default' : 'ghost'}
                    size="sm"
                    className={`gap-2 transition-all ${
                      location.pathname === item.path 
                        ? 'bg-[#006039] hover:bg-[#005432] text-white shadow-md' 
                        : 'hover:bg-[#006039]/10 hover:text-[#006039] dark:hover:bg-[#006039]/20 dark:hover:text-emerald-400'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Button>
                </Link>
              );
            })}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-1 hover:bg-[#006039]/10 hover:text-[#006039] dark:hover:bg-[#006039]/20 dark:hover:text-emerald-400"
                >
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline">{(i18n.language || 'en').split('-')[0].toUpperCase()}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="backdrop-blur-xl bg-white/95 dark:bg-slate-950/95 border-slate-200/50 dark:border-slate-800/50 shadow-xl"
              >
                <DropdownMenuItem onClick={() => changeLanguage('de')} className="cursor-pointer hover:bg-[#006039]/10 dark:hover:bg-[#006039]/20">
                  🇩🇪 Deutsch
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('en')} className="cursor-pointer hover:bg-[#006039]/10 dark:hover:bg-[#006039]/20">
                  🇬🇧 English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme Switcher */}
            <ThemeSwitcher />

            {/* User Profile Menu - Premium Style */}
            {isLoggedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="gap-2 p-1 h-auto hover:bg-transparent"
                  >
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Avatar className="h-9 w-9 border-2 border-ascetic-500 shadow-lg">
                        <AvatarFallback className="bg-gradient-to-br from-ascetic-600 via-ascetic-500 to-emerald-500 text-white font-semibold text-sm">
                          {getUserInitials()}
                        </AvatarFallback>
                      </Avatar>
                    </motion.div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-64 backdrop-blur-xl bg-white/95 dark:bg-slate-950/95 border-slate-200/50 dark:border-slate-800/50 shadow-xl"
                >
                  <DropdownMenuLabel className="pb-2">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12 border-2 border-ascetic-500">
                        <AvatarFallback className="bg-gradient-to-br from-ascetic-600 via-ascetic-500 to-emerald-500 text-white font-semibold">
                          {getUserInitials()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{userEmail}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Crown className="h-3 w-3 text-violet-500" />
                          Premium User
                        </p>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-3 cursor-pointer">
                      <User className="h-4 w-4" />
                      <span>Profil</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/favorite-quotes" className="flex items-center gap-3 cursor-pointer">
                      <Heart className="h-4 w-4" />
                      <span>{i18n.language === 'de' ? 'Lieblingszitate' : 'Favorite Quotes'}</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/subscription" className="flex items-center gap-3 cursor-pointer">
                      <Crown className="h-4 w-4" />
                      <span>Abo verwalten</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="flex items-center gap-3 cursor-pointer text-red-600 dark:text-red-400"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Abmelden</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button 
                  size="sm" 
                  className="bg-[#006039] hover:bg-[#005432] text-white shadow-lg hover:shadow-xl transition-all"
                >
                  <User className="h-4 w-4 mr-2" />
                  Login
                </Button>
              </Link>
            )}

            {/* Mobile Menu */}
            <div className="lg:hidden">
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="p-2 hover:bg-[#006039]/10 dark:hover:bg-[#006039]/20"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent 
                  side="right" 
                  className="w-80 backdrop-blur-xl bg-white/95 dark:bg-slate-950/95 border-slate-200/50 dark:border-slate-800/50 flex flex-col"
                >
                  <SheetHeader className="flex-shrink-0">
                    <SheetTitle className="flex items-center gap-3">
                      <img 
                        src="/logo.png" 
                        alt="AsceticLife Logo" 
                        className="h-10 w-10 object-contain rounded-lg"
                      />
                      <span className="text-[#006039] dark:text-emerald-400 font-bold">
                        AsceticLife
                      </span>
                    </SheetTitle>
                  </SheetHeader>
                  <div className="flex-1 overflow-y-auto mt-4 -mx-2 px-2">
                    <div className="space-y-1 pb-4">
                      {navItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
                              isActive
                                ? 'bg-[#006039] text-white shadow-md'
                                : 'hover:bg-[#006039]/10 dark:hover:bg-[#006039]/20'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            <span className="font-medium text-sm">{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Fixed bottom section for login/logout */}
                  <div className="flex-shrink-0 border-t pt-3 mt-2">
                    {isLoggedIn ? (
                      <Button 
                        variant="outline" 
                        className="w-full justify-start gap-3 text-red-600"
                        onClick={() => { handleLogout(); setIsOpen(false); }}
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Abmelden</span>
                      </Button>
                    ) : (
                      <Link to="/login" onClick={() => setIsOpen(false)}>
                        <Button className="w-full bg-[#006039] hover:bg-[#005432]">
                          <User className="h-4 w-4 mr-2" />
                          Login
                        </Button>
                      </Link>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Glow Effect */}
      {isLoggedIn && (
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#006039]/50 to-transparent" />
      )}
    </motion.nav>
  );
};